import { useCallback, useMemo } from 'react';
import { getTestResultsTableColumns } from '@elementary/common-ui/pages';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { PagedQueryOptions } from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import { ColumnFilterOption } from '@elementary/common-ui/components/Table/types';
import { distinctArrayBy } from '@elementary/common-ui/utils/common';
import { parseGraphqlFilterOptions } from '@elementary/common-ui/utils/graphqlFilters';
import { TestExecutionField } from '@elementary/common-ui/services/gql/types';
import {
  TEST_RESULTS,
  TEST_RESULTS_FILTER_OPTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import { DEFAULT_DAYS_BACK, PAGE_SIZE } from '@elementary/common-ui/constants';
import { TEST_RESULTS_GROUP_BY } from '@elementary/common-ui/constants/urlParams';
import {
  parseApiTestResultTableData,
  type ApiTestResultsTableData,
  type TestResultsTableData,
} from '@elementary/common-ui/components';
import { TestResultsChips } from '@elementary/common-ui/components/ChipList';
import useGroupByParam from 'shared/hooks/useGroupByParam';
import useGroupedTableData from 'shared/hooks/useGroupedTableData';
import {
  ApiTestResultsFilterOptionsResponse,
  ApiTestResultsTableDataPagedResponse,
  getTestResultsFilterOptionsQuery,
  getTestResultsTableDataQuery,
} from '../api';
import { COLUMN_TO_FIELD_MAP, FIELD_TO_COLUMN_MAP } from '../consts';
import useTestResultsQueryContext from '../useTestResultsQueryContext';
import useGroupStatusSummaries from '../useGroupStatusSummaries';

const STATUS_COLUMN_ID = 'latestRunStatus';

const mapTestResultRows = (
  items: ApiTestResultsTableData[],
): TestResultsTableData[] =>
  distinctArrayBy(items, 'execution.subTestUniqueId').map(
    parseApiTestResultTableData,
  );

type Args = {
  search: string;
};

const useGroupedTestResults = ({ search }: Args) => {
  const { groupBy: groupByColumnId, onGroupByChange } = useGroupByParam({
    paramKey: TEST_RESULTS_GROUP_BY,
  });
  const isGrouped = !!groupByColumnId;

  const {
    envid,
    selectedAssetIds,
    selectedTestIds,
    selectedIdsHash,
    hasSelectedNodes,
    urlFiltersDebounced,
    testExecutionFilters: baseTestExecutionFilters,
    urlSort,
    sort,
    filterOptions,
    unscopedFilterOptions,
    isFilterOptionsFetching,
  } = useTestResultsQueryContext();

  const groupEnabled = isGrouped && hasSelectedNodes;

  const groupableColumnIds = useMemo(
    () =>
      Object.keys(unscopedFilterOptions).filter(
        (id) => (unscopedFilterOptions[id]?.length ?? 0) > 0,
      ),
    [unscopedFilterOptions],
  );

  const { data: groupOptionsData, isFetching: isGroupOptionsFetching } =
    useGraphqlQuery<ApiTestResultsFilterOptionsResponse>({
      queryKey: [
        TEST_RESULTS_FILTER_OPTIONS,
        envid,
        selectedIdsHash,
        DEFAULT_DAYS_BACK,
        'grouped',
        groupByColumnId,
        baseTestExecutionFilters,
        search,
      ],
      query: getTestResultsFilterOptionsQuery,
      variables: {
        testIds: selectedTestIds.length ? selectedTestIds : null,
        assetIds: selectedAssetIds.length ? selectedAssetIds : null,
        daysBack: DEFAULT_DAYS_BACK,
        search: search || null,
        testExecutionFilters: baseTestExecutionFilters,
      },
      enabled: groupEnabled,
      envId: envid,
    });

  const groupValues = useMemo<ColumnFilterOption[]>(() => {
    if (!groupByColumnId) return [];
    const parsed = parseGraphqlFilterOptions<TestExecutionField>(
      groupOptionsData?.latestTestExecutionsFilterOptions || [],
      FIELD_TO_COLUMN_MAP,
    );
    return parsed[groupByColumnId] ?? [];
  }, [groupOptionsData, groupByColumnId]);

  const showStatusSummary = isGrouped && groupByColumnId !== STATUS_COLUMN_ID;

  const statusValues = useMemo(
    () => (filterOptions[STATUS_COLUMN_ID] ?? []).map((option) => option.value),
    [filterOptions],
  );

  const statusTotalsByGroup = useGroupStatusSummaries({
    groupByColumnId: showStatusSummary ? groupByColumnId : null,
    statusValues,
    selectedAssetIds,
    selectedTestIds,
    selectedIdsHash,
    baseTestExecutionFilters,
    search,
    envid,
    enabled: groupEnabled && showStatusSummary,
  });

  const field = groupByColumnId
    ? COLUMN_TO_FIELD_MAP[groupByColumnId]
    : undefined;

  const formatGroupTitle = useMemo(() => {
    if (!groupByColumnId) return (value: string) => value;
    const columns = getTestResultsTableColumns([], filterOptions);
    const groupColumn = columns.find((c) => c.id === groupByColumnId);
    return (value: string) => groupColumn?.filterRenderer?.(value) ?? value;
  }, [filterOptions, groupByColumnId]);

  const buildQueryOptions = useCallback(
    (
      value: string,
    ): PagedQueryOptions<
      ApiTestResultsTableData,
      ApiTestResultsTableDataPagedResponse
    > => ({
      queryKey: [
        TEST_RESULTS,
        envid,
        selectedIdsHash,
        DEFAULT_DAYS_BACK,
        urlFiltersDebounced,
        urlSort,
        search,
        field,
        value,
      ],
      query: getTestResultsTableDataQuery,
      variables: {
        testIds: selectedTestIds.length ? selectedTestIds : null,
        assetIds: selectedAssetIds.length ? selectedAssetIds : null,
        daysBack: DEFAULT_DAYS_BACK,
        testExecutionFilters: field
          ? [
              ...baseTestExecutionFilters,
              { property: field, operator: 'equals', value: [value] },
            ]
          : baseTestExecutionFilters,
        sort,
        search: !search ? null : search,
        limit: PAGE_SIZE,
      },
      select: (d) => d.latestTestExecutionsPaged,
      enabled: groupEnabled && !!field,
      envId: envid,
    }),
    [
      envid,
      selectedIdsHash,
      selectedTestIds,
      selectedAssetIds,
      baseTestExecutionFilters,
      urlFiltersDebounced,
      urlSort,
      sort,
      search,
      groupEnabled,
      field,
    ],
  );

  const getGroupDecorations = useCallback(
    (value: string) => ({
      metadataRenderer: showStatusSummary
        ? () => (
            <TestResultsChips
              totals={statusTotalsByGroup[value]}
              size="medium"
              hideEmptyState
            />
          )
        : undefined,
    }),
    [showStatusSummary, statusTotalsByGroup],
  );

  const {
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    isAnyFetchingWithRows,
  } = useGroupedTableData({
    groupBy: groupByColumnId,
    groupValues,
    enabled: !!field,
    formatGroupTitle,
    buildQueryOptions,
    mapItems: mapTestResultRows,
    getGroupDecorations,
  });

  const isFetching =
    isFilterOptionsFetching || isGroupOptionsFetching || isAnyFetchingWithRows;

  return {
    groupBy: groupByColumnId,
    onGroupByChange,
    isGrouped,
    groupableColumnIds,
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    filterOptions,
    isFetching,
    isFilterOptionsFetching,
  };
};

export default useGroupedTestResults;
